import React, {useContext, useEffect, useMemo} from "react";
import {UiSelect} from "../ui-components/select/uiSelect";
import {UiOption} from "../ui-components/select/option/uiOption";
import {GameMode} from "../../models/GameMode";
import {GameApi} from "../../services/api/GameApi";
import {useTranslation} from "react-i18next";
import {UiSegementedButtons} from "../ui-components/segementedButtons/UiSegementedButtons";
import {UiSegementedButton} from "../ui-components/segementedButtons/UiSegementedButton";
import {capitalize} from "../../utils";
import {AppContext} from "../authProvider/AuthProvider";

export enum PlatformType {
    web = "web",
    mobile = "mobile"
}

type SelectGameModeType = {
    className?: string;
    onChange: (platform: PlatformType, gameModeId: number) => Promise<void>;
    onApiError?: () => void;
}

export const SelectGameMode: React.FC<SelectGameModeType> = (props) => {
    const {t} = useTranslation();
    const {errorToast} = useContext(AppContext);

    const [platform, setPlatform] = React.useState<PlatformType>(PlatformType.web);
    const [gameThemes, setGameThemes] = React.useState<GameMode[]>([]);
    const [gameModes, setGameModes] = React.useState<GameMode[]>([]);
    const [gameModesDifficulties, setGameModesDifficulties] = React.useState<GameMode[]>([]);

    const [selectedGameTheme, setSelectedGameTheme] = React.useState<number|null>(null);
    const [selectedGameMode, setSelectedGameMode] = React.useState<number|null>(null);
    const [selectedGameModeDifficulty, setSelectedGameModeDifficulty] = React.useState<number|null>(null);

    const gameModesFiltered = useMemo(() => {
        return gameModes
            .filter((gameMode) => gameMode.idGameTheme === selectedGameTheme)
    }, [selectedGameTheme, gameModes]);

    const difficultiesFiltered = useMemo(() => {
        return gameModesDifficulties
            .filter((gameMode) => gameMode.idGameTheme === selectedGameMode);
    }, [selectedGameMode, gameModesDifficulties]);

    useEffect(() => {
        setSelectedGameMode(gameModesFiltered[0]?.id || null);
    }, [selectedGameTheme]);

    useEffect(() => {
        setSelectedGameModeDifficulty(difficultiesFiltered[0]?.id || null);
    }, [selectedGameMode]);

    useEffect(function fetchAllGameModes() {
        GameApi.getAllGameModes()
            .then((data) => {
                const gameThemes = data.filter((gameMode) => gameMode.idGameTheme === null)
                const gameModes = data.filter((gameMode) => gameMode.idGameTheme !== null && gameMode.idDifficulty === null)
                const gameModesDifficulties = data.filter((gameMode) => gameMode.idGameTheme !== null && gameMode.idDifficulty !== null)
                
                setGameThemes(gameThemes);
                setGameModes(gameModes);
                setGameModesDifficulties(gameModesDifficulties);
                
                setSelectedGameTheme(gameThemes[0].id)
                setSelectedGameMode(gameModes[0].id)
                setSelectedGameModeDifficulty(gameModesDifficulties[0].id)
            })
            .catch(() => {
                errorToast();
                if (props.onApiError) {
                    props.onApiError();
                }
            });
    }, []);

    useEffect(function sendDataToParent(){
        if (selectedGameModeDifficulty === null) {
            return;
        }

        props.onChange(platform, selectedGameModeDifficulty).then()
    }, [platform, selectedGameModeDifficulty]);

    return (
        <div className={(props.className ? `${props.className} ` : "") + "flex flex-col w-full md:w-fit items-end gap-2"}>

            {/* platform */}
            <UiSegementedButtons
                className="w-full lg:w-fit"
                onChange={(key) => setPlatform(key as PlatformType)}
            >
                <UiSegementedButton key={PlatformType.web} label={capitalize(t("global.web"))}/>
                <UiSegementedButton key={PlatformType.mobile} label={capitalize(t("global.mobile"))}/>
            </UiSegementedButtons>

            {/* gameTheme */}
            <UiSegementedButtons
                className="w-full lg:w-fit"
                onChange={(key) => {
                    setSelectedGameTheme(parseInt(key.toString()))
                }}
            >
                {gameThemes.map((gameTheme) => (
                    <UiSegementedButton key={gameTheme.id} label={gameTheme.name!}/>
                ))}
            </UiSegementedButtons>

            {/* gameMode */}
            {(selectedGameTheme !== null && gameModesFiltered.length > 1) && <UiSelect
                className="w-full lg:w-fit"
                search={true}
                defaultValue={gameModesFiltered[0]?.id}
                onChange={(value) => {
                    setSelectedGameMode(parseInt(value))
                }}
                onOpenClearSearch={true}
            >
                {gameModesFiltered
                    .map((gameMode) => (
                    <UiOption key={gameMode.id} value={gameMode.id}>{gameMode.name}</UiOption>
                ))}
            </UiSelect>}

            {/* gameModeDifficulty */}
            {difficultiesFiltered.length > 1 && <UiSegementedButtons
                className="w-full lg:w-fit"
                onChange={(key) => {
                    setSelectedGameModeDifficulty(parseInt(key.toString()))}}
            >
                {difficultiesFiltered
                    .map((gameMode) => (
                        <UiSegementedButton key={gameMode.id} label={gameMode.difficulty!.name!}/>
                    ))}
            </UiSegementedButtons>}
        </div>
    )
}
